/**
 * StartProjectPreview — visual mockup of the Start Project / Break Ground feature.
 *
 * NOT a live page — this is a static preview showing every UI surface
 * the owner will touch + every email that fires, so the owner can sign
 * off on the design before we build the real thing.
 *
 * Route: /start-project-preview (no auth)
 *
 * NOTE: This file intentionally avoids `dangerouslySetInnerHTML`. The mock
 * email's `intro` and `bodyHtml` props accept React nodes, not HTML strings.
 */
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Rocket, CheckCircle2, Calendar, AlertTriangle, Bell, Hammer, Mail,
  Building2, Clock, Users, FileText, Sparkles, ArrowRight, MoreVertical,
  TrendingUp, ChevronDown, Construction,
} from 'lucide-react';

const BLUE = '#2563EB';
const SAND = '#F4E4BC';

// Convenience: emphasized text on the dark email hero
const E = ({ children }) => <strong style={{ color: '#ffffff' }}>{children}</strong>;

// =====================================================================
// Reusable: device-frame style email preview
// =====================================================================
const EmailMock = ({ subject, preheader, eyebrow, title, intro, sidePanel, bodyNode, cta, badge }) => (
  <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm">
    {/* Inbox row */}
    <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 text-xs">
      <p className="font-semibold text-slate-800 truncate">{subject}</p>
      <p className="text-slate-500 truncate">{preheader}</p>
    </div>
    {/* Wide Hybrid hero */}
    <div className="p-3" style={{ background: '#0B2447' }}>
      <div className="rounded-md overflow-hidden bg-white shadow-lg">
        <div className="p-4" style={{ background: `linear-gradient(135deg, #1d4ed8 0%, ${BLUE} 60%, #3b82f6 100%)` }}>
          <div className="flex gap-3">
            <div className="flex-1">
              {badge && (
                <span className="inline-block text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white mb-1.5" style={{ background: 'rgba(255,255,255,0.18)' }}>
                  {badge}
                </span>
              )}
              <p className="text-[8px] font-bold uppercase tracking-wider text-blue-100 mb-1">{eyebrow}</p>
              <p className="text-white font-bold text-base leading-tight">{title}</p>
              <p className="text-blue-50 text-xs mt-1.5 leading-snug">{intro}</p>
              {cta && (
                <button className="mt-3 inline-flex items-center text-[10px] font-bold px-3 py-1.5 rounded-md bg-white text-blue-700 shadow-sm">
                  {cta} <ArrowRight className="w-2.5 h-2.5 ml-1" />
                </button>
              )}
            </div>
            {sidePanel && (
              <div className="w-32 flex-shrink-0 rounded-md p-2 backdrop-blur-sm" style={{ background: 'rgba(255,255,255,0.12)' }}>
                {sidePanel.map(([k, v]) => (
                  <div key={k} className="mb-1.5 last:mb-0">
                    <p className="text-[7px] font-bold uppercase tracking-wider text-blue-100">{k}</p>
                    <p className="text-white text-[10px] font-bold leading-tight">{v}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {bodyNode && (
          <div className="px-4 py-3 text-[10px] text-slate-600 leading-relaxed">{bodyNode}</div>
        )}
        <div className="border-t border-slate-100 px-4 py-2 bg-slate-50 text-[9px] text-slate-400 text-center">
          drawready.com · Built for Real Estate Developers
        </div>
      </div>
    </div>
  </div>
);

// =====================================================================
// Section header
// =====================================================================
const SectionHeader = ({ num, title, subtitle, tone = 'blue' }) => {
  const tones = {
    blue: 'bg-blue-600 text-white',
    amber: 'bg-amber-500 text-white',
    red: 'bg-red-500 text-white',
    emerald: 'bg-emerald-600 text-white',
    slate: 'bg-slate-700 text-white',
  };
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${tones[tone]}`}>
        {num}
      </div>
      <div>
        <h3 className="text-lg font-bold text-slate-900">{title}</h3>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
    </div>
  );
};

// =====================================================================
// Main page
// =====================================================================
export default function StartProjectPreview() {
  const [openDropdown, setOpenDropdown] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC' }}>
      {/* Top intro bar */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${BLUE}, #3b82f6)` }}>
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Start Project / Break Ground — Visual Preview</h1>
              <p className="text-xs text-slate-500">Every UI surface and every email this feature touches. Not interactive — just design sign-off.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 space-y-14">

        {/* ============================================================
            SECTION 1 — Project page BEFORE start (status='planning')
        ============================================================ */}
        <section data-testid="section-before-start">
          <SectionHeader num="1" title="Project page — before Start" subtitle="Status = planning · Big gradient banner invites the owner to press Start" tone="blue" />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            {/* UI mock */}
            <Card className="lg:col-span-3 p-0 overflow-hidden">
              {/* Fake project page header */}
              <div className="bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Project</p>
                  <p className="text-base font-bold text-slate-900">1615 38th St SE — New Build</p>
                </div>
                <div className="relative">
                  <Button variant="ghost" size="sm" onClick={() => setOpenDropdown(!openDropdown)}>
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                  {openDropdown && (
                    <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-md shadow-lg z-10">
                      <button className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2">
                        <Sparkles className="w-3 h-3" /> AI Predict
                      </button>
                      <button className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2">
                        <TrendingUp className="w-3 h-3" /> Financial Analysis
                      </button>
                      <div className="border-t border-slate-100 my-1" />
                      <button className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 flex items-center gap-2 text-blue-700 font-semibold">
                        <Rocket className="w-3 h-3" /> Start Project
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {/* THE NEW BANNER */}
              <div className="p-5" style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 60%, #3b82f6 100%)' }}>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.18)' }}>
                    <Hammer className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-blue-100 mb-1">Ready to break ground?</p>
                    <h2 className="text-white text-xl font-bold leading-tight">Press Start to notify all contractors.</h2>
                    <p className="text-blue-50 text-xs mt-1.5 leading-relaxed">
                      Phase 1 will auto-status to <strong className="text-white">in progress</strong>, your full contractor roster gets a personalized
                      "we broke ground" email with their phase dates highlighted, and the schedule clock starts running.
                    </p>
                    <div className="flex items-center gap-3 mt-4">
                      <Button className="bg-white hover:bg-blue-50 text-blue-700 font-bold shadow-md" data-testid="mock-start-project-btn">
                        <Rocket className="w-4 h-4 mr-1.5" /> Start Project
                      </Button>
                      <span className="text-[11px] text-blue-100">Expected: 06/01/2026 · 6 contractors will be notified</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-5 py-3 bg-slate-50 text-[11px] text-slate-500">
                <strong className="text-slate-700">Tip:</strong> Press Start when boots actually hit the ground — not just when the permit is approved. This keeps the contractor schedule honest.
              </div>
            </Card>

            {/* Spec callouts */}
            <div className="lg:col-span-2 space-y-3">
              <Card className="p-4 border-l-4 border-blue-500">
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Where it lives</p>
                <p className="text-xs text-slate-700 mt-1">Banner top of project page <em>(only when status=planning)</em>. Also reachable from the 3-dot menu.</p>
              </Card>
              <Card className="p-4 border-l-4 border-emerald-500">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">What firing Start does</p>
                <ul className="text-xs text-slate-700 mt-1 space-y-1 list-disc list-inside">
                  <li>project.status → <code className="bg-slate-100 px-1 rounded">active</code></li>
                  <li>project.project_started_at = now</li>
                  <li>Phase 1 status → <code className="bg-slate-100 px-1 rounded">in_progress</code></li>
                  <li>📧 Blast email to every contractor on every phase</li>
                  <li>📌 cascade_events log entry</li>
                </ul>
              </Card>
              <Card className="p-4 border-l-4 border-amber-500">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600">After Start fires</p>
                <p className="text-xs text-slate-700 mt-1">Banner disappears. Replaced by a green "Project active since 06/01/2026" pill near the project title.</p>
              </Card>
            </div>
          </div>
        </section>

        {/* ============================================================
            SECTION 2 — Owner reminders (day-before, day-of, overdue)
        ============================================================ */}
        <section data-testid="section-reminders">
          <SectionHeader num="2" title="Owner reminders — three escalating nudges" subtitle="So you never forget to press Start" tone="amber" />

          {/* Yellow Command Center nudge card */}
          <Card className="p-5 mb-5" style={{ background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)', borderColor: '#F59E0B' }}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#F59E0B' }}>
                <Bell className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Dashboard · Command Center · Today</p>
                <h3 className="text-amber-900 text-base font-bold mt-0.5">1615 38th St SE is expected to break ground tomorrow.</h3>
                <p className="text-amber-800 text-xs mt-1">Press <strong>Start Project</strong> in the morning so your 6 contractors get their kickoff emails on time.</p>
                <div className="flex items-center gap-2 mt-3">
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                    <Rocket className="w-3.5 h-3.5 mr-1.5" /> Open project
                  </Button>
                  <Button size="sm" variant="outline" className="border-amber-400 text-amber-800">
                    Push start date
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Three escalating emails */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Day-before email · Recipient: Owner</p>
              <EmailMock
                subject="📅 1615 38th St SE breaks ground tomorrow"
                preheader="Press Start in the morning so contractors get their kickoff emails"
                eyebrow="Heads up · Tomorrow"
                title="Ready to break ground?"
                intro={<>Your project is scheduled to start <E>tomorrow morning</E>. Make sure to press Start so your subs get notified on time.</>}
                sidePanel={[['Project', '1615 38th St SE'], ['Start', '06/01/2026'], ['Crew', '6 contractors']]}
                cta="Open project"
              />
            </div>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Day-of email · Recipient: Owner</p>
              <EmailMock
                badge="ACTION TODAY"
                subject="🏗️ 1615 38th St SE — press Start today"
                preheader="Crew is expected on site today. Press Start to notify them."
                eyebrow="Today · Action required"
                title="Boots on the ground today."
                intro={<>Your project should be breaking ground <E>today</E>. Press Start now to fire the kickoff cascade.</>}
                sidePanel={[['Project', '1615 38th St SE'], ['Date', '06/01/2026'], ['Phase 1', 'Site Prep']]}
                cta="Press Start now"
              />
            </div>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-red-500 mb-2">Day-after (overdue) · 1c escalation</p>
              <EmailMock
                badge="OVERDUE"
                subject="⚠️ 1615 38th St SE — overdue to start"
                preheader="Crew confirmed on-track but Start hasn't been pressed"
                eyebrow="Overdue · Confirm or push"
                title="Phase 1 may already be live."
                intro={<>Your foundation contractor confirmed they're <E>on track</E>, but the project hasn't been marked Started. Confirm or push the date.</>}
                sidePanel={[['Status', 'Overdue 1d'], ['Last ping', 'Block 7 ✓'], ['Action', 'Confirm Start']]}
                cta="Confirm Start"
              />
            </div>
          </div>

          {/* Drift detection callout */}
          <Card className="mt-5 p-4 border-l-4 border-red-500 bg-red-50">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-red-900">Smart drift detection (option 1c)</p>
                <p className="text-xs text-red-800 mt-1">
                  If any contractor confirms "on track" via the cadence ping AFTER the project start_date has passed AND you haven't pressed Start,
                  you get the OVERDUE email immediately (regardless of the day-after schedule). This catches "ghost-started" projects in real time.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* ============================================================
            SECTION 3 — Contractor blast email
        ============================================================ */}
        <section data-testid="section-blast">
          <SectionHeader num="3" title="Break-ground blast — every contractor gets one" subtitle="Personalized · their phase highlighted in the schedule grid" tone="emerald" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Email to: framing@block7.com · Their phase highlighted</p>
              <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 text-xs">
                  <p className="font-semibold text-slate-800">🏗️ 1615 38th St SE broke ground today — your scope is live</p>
                  <p className="text-slate-500">Your phase (Framing) starts 06/15. Here's the full timeline.</p>
                </div>
                <div className="p-3" style={{ background: '#0B2447' }}>
                  <div className="rounded-md overflow-hidden bg-white">
                    <div className="p-4" style={{ background: 'linear-gradient(135deg, #059669, #10B981)' }}>
                      <p className="text-[8px] font-bold uppercase tracking-wider text-emerald-100">Project broke ground · 06/01/2026</p>
                      <p className="text-white font-bold text-base">We broke ground today.</p>
                      <p className="text-emerald-50 text-xs mt-1.5">Hi <strong className="text-white">Block 7 Contracting</strong>, the project is officially live. Your phase <strong className="text-white">(Framing)</strong> starts <strong className="text-white">06/15/2026</strong>.</p>
                      <button className="mt-3 inline-flex items-center text-[10px] font-bold px-3 py-1.5 rounded-md bg-white text-emerald-700">
                        Open your workspace <ArrowRight className="w-2.5 h-2.5 ml-1" />
                      </button>
                    </div>
                    {/* Schedule grid in email */}
                    <div className="px-4 py-3">
                      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-2">Full project schedule</p>
                      <div className="space-y-1">
                        {[
                          ['1', 'Site Prep', '06/01 → 06/14', false, 'in progress'],
                          ['2', 'Foundation', '06/02 → 06/14', false, ''],
                          ['3', 'Framing', '06/15 → 07/15', true, '← you'],
                          ['4', 'Rough-in', '07/16 → 08/02', false, ''],
                          ['5', 'Drywall', '08/03 → 08/20', false, ''],
                        ].map(([n, name, dates, mine, tag]) => (
                          <div key={n} className={`flex items-center justify-between px-2 py-1 rounded text-[10px] ${mine ? 'bg-emerald-50 border border-emerald-300' : 'bg-slate-50'}`}>
                            <div className="flex items-center gap-2">
                              <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold ${mine ? 'bg-emerald-600 text-white' : 'bg-slate-300 text-slate-700'}`}>{n}</span>
                              <span className={`font-semibold ${mine ? 'text-emerald-900' : 'text-slate-700'}`}>{name}</span>
                              {tag && <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${mine ? 'bg-emerald-600 text-white' : 'bg-blue-100 text-blue-700'}`}>{tag.toUpperCase()}</span>}
                            </div>
                            <span className={`text-[9px] ${mine ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>{dates}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="border-t border-slate-100 px-4 py-2 bg-slate-50 text-[9px] text-slate-400 text-center">
                      drawready.com · Built for Real Estate Developers
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* What goes in this email */}
            <div className="space-y-3">
              <Card className="p-4 border-l-4 border-emerald-500">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">What every contractor sees</p>
                <ul className="text-xs text-slate-700 mt-1 space-y-1 list-disc list-inside">
                  <li>"We broke ground" hero (emerald → not blue, signals project milestone vs. routine cascade)</li>
                  <li>Project name + address + start date</li>
                  <li><strong>Their own phase highlighted</strong> in emerald with "← you" tag</li>
                  <li>Full schedule grid so they see who's around them</li>
                  <li>Owner contact info in the footer (name, email, phone)</li>
                  <li>Big CTA to their contractor workspace</li>
                </ul>
              </Card>
              <Card className="p-4 border-l-4 border-blue-500">
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Owner gets a confirmation too</p>
                <p className="text-xs text-slate-700 mt-1">"✅ 1615 38th St SE — kickoff sent to 6 contractors" — so you have proof in your inbox the cascade fired.</p>
              </Card>
              <Card className="p-4 border-l-4 border-slate-400">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Cascade test mode honored</p>
                <p className="text-xs text-slate-700 mt-1">If your test mode is ON, the blast routes to your inbox with <code className="bg-slate-100 px-1 rounded">[TEST → contractor]</code> prefix — same safety as the existing cascade.</p>
              </Card>
            </div>
          </div>
        </section>

        {/* ============================================================
            SECTION 4 — Phase-level "you're up" handoffs
        ============================================================ */}
        <section data-testid="section-phase-handoff">
          <SectionHeader num="4" title="Phase-level handoffs — hybrid auto + manual" subtitle="Phase 1 auto-starts with project. Subsequent phases stay manual." tone="slate" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Phase 1 auto */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm">1</span>
                <div>
                  <p className="text-sm font-bold text-slate-900">Phase 1 · Site Prep</p>
                  <span className="inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Auto-started</span>
                </div>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">When you press <strong>Start Project</strong>, Phase 1 immediately flips to <code className="bg-slate-100 px-1 rounded">in_progress</code>. No extra click needed.</p>
              <p className="text-[10px] text-slate-500 mt-2">Rationale: you can't start the project without Phase 1 also starting. They're the same event.</p>
            </Card>

            {/* Phase 2+ manual */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-7 h-7 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-sm">2+</span>
                <div>
                  <p className="text-sm font-bold text-slate-900">Phase 2 · Foundation · onward</p>
                  <span className="inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Manual Start</span>
                </div>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">Each subsequent phase requires you to manually press <strong>Start Phase</strong> on the phase row (button already exists today). That fires the "you're up" email to the contractor for that phase.</p>
              <p className="text-[10px] text-slate-500 mt-2">Owner gets the same day-before + day-of reminders for each phase's expected start_date.</p>
            </Card>
          </div>

          {/* Phase ready-to-start contractor email */}
          <div className="mt-5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Email when owner clicks Start Phase on Phase 3 (Framing)</p>
            <div className="max-w-lg">
              <EmailMock
                subject="🔨 You're up — Framing at 1615 38th St SE"
                preheader="Phase 2 wrapped. Your scope kicks off Monday 06/15."
                eyebrow="You're up · Framing"
                title="Get your crew on site Monday."
                intro={<>Hi <E>Block 7</E>, Foundation wrapped today. Your scope (<E>Framing</E>) starts Monday <E>06/15/2026</E>.</>}
                sidePanel={[['Phase', 'Framing'], ['Start', '06/15/2026'], ['Duration', '~30 days']]}
                cta="Open your workspace"
                bodyNode={<><strong>Reminder:</strong> Submit your daily reports + lien waiver via your portal. Materials should already be on site per the 4-week procurement reminder you got earlier.</>}
              />
            </div>
          </div>
        </section>

        {/* ============================================================
            SECTION 5 — Legacy projects (option 2A + 2B)
        ============================================================ */}
        <section data-testid="section-legacy">
          <SectionHeader num="5" title="Existing projects — silent backfill + one-time opt-in banner" subtitle="So your current customers can opt in without disruption" tone="blue" />
          <Card className="p-5 mb-4 bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-blue-900">New feature available on your active projects</p>
                <p className="text-xs text-blue-800 mt-1">
                  We just shipped Start Project notifications. Your active projects are already in flight — we won't blast emails retroactively.
                  But you can enable the day-before / day-of reminders + phase handoff emails for your remaining phases.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                    Enable for active projects
                  </Button>
                  <Button size="sm" variant="outline">Maybe later</Button>
                </div>
              </div>
            </div>
          </Card>
          <div className="text-xs text-slate-600 leading-relaxed">
            <strong>Behind the scenes:</strong> any project with a <code className="bg-slate-100 px-1 rounded">start_date</code> already in the past gets silently flagged
            <code className="bg-slate-100 px-1 rounded mx-1">project_started_at = start_date</code> so the system doesn't think they're un-started. No emails fire.
            New phase-level reminders activate going forward.
          </div>
        </section>

        {/* ============================================================
            CHEAT SHEET
        ============================================================ */}
        <section className="border-t pt-8">
          <SectionHeader num="✓" title="Cheat sheet — everything this feature does" tone="emerald" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div className="space-y-2">
              <p className="font-bold text-slate-800">🆕 New backend fields</p>
              <ul className="list-disc list-inside text-slate-600 space-y-1">
                <li>projects.project_started_at <em>(ISO timestamp)</em></li>
                <li>projects.project_started_by <em>(user_id who clicked Start)</em></li>
              </ul>
              <p className="font-bold text-slate-800 mt-3">🆕 New endpoints</p>
              <ul className="list-disc list-inside text-slate-600 space-y-1">
                <li>POST /api/projects/:id/start</li>
                <li>POST /api/projects/:id/phases/:idx/start <em>(extends existing)</em></li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="font-bold text-slate-800">📅 New cron job</p>
              <ul className="list-disc list-inside text-slate-600 space-y-1">
                <li>Daily 8:00am owner timezone scan</li>
                <li>Finds projects with start_date == tomorrow → day-before email + bell</li>
                <li>Finds projects with start_date == today AND not started → day-of email + bell</li>
                <li>Finds projects overdue + contractor signaled on-track → 1c drift email</li>
              </ul>
              <p className="font-bold text-slate-800 mt-3">🔔 Bell-icon notification center</p>
              <ul className="list-disc list-inside text-slate-600 space-y-1">
                <li>Persistent dropdown in header (Phase D of the sprint)</li>
                <li>All Start/Start Phase/material procurement nudges land here</li>
                <li>Click → opens relevant project</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t pt-6 text-center text-xs text-slate-500">
          <p>Once you sign off, I'll implement in this order: backend endpoints + cron → start banner UI → break-ground blast email → reminder emails → bell notification center.</p>
          <p className="mt-1">Estimated build: ~half a sprint. Test coverage will mirror the e-signature flow (full pytest + Playwright).</p>
        </footer>
      </div>
    </div>
  );
}
